import * as React from 'react';

import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';

// @ts-ignore
import PeopleListViewAllAnnouncementsDataContainer from 'components/PeopleList/PeopleListViewAllAnnouncements';
// @ts-ignore
import {
  GroupPeopleListBatchActions,
  OrgPeopleListBatchActions,
} from 'components/PeopleList/PeopleListBatchActions';
import {
  UnclaimedGroupListBatchActions,
  UnclaimedGroupBatchProps,
} from 'groups/components/GroupList/GroupListBatchActions';

import {
  GroupProfileProps,
  GroupProfileType,
  // @ts-ignore
} from 'groups/components/GroupProfile';
import UnclaimedGroupProfile, {
  PartialUnclaimedGroupProfileType,
} from 'groups/components/UnclaimedGroupProfile';
import {
  UserProfileProps,
  UserProfile,
  UserProfilePartial,
  // @ts-ignore
} from 'users/components/UsersProfile';
// @ts-ignore
import { GroupBatchProps, OrgBatchProps } from 'components/PeopleList/PeopleListBatchActions';
import { OrganizationRole } from 'globalTypes';

export const ORG_BATCH = 'org-batch';
export const GROUP_BATCH = 'group-batch';
export const GROUP_PROFILE = 'group-profile';
export const UNCLAIMED_GROUP_BATCH = 'unclaimed-group-batch';
export const UNCLAIMED_GROUP_PROFILE = 'unclaimed-group-profile';
export const USER_PROFILE = 'user-profile';
const ANNOUNCEMENTS = 'view-all-announcements';
const WORKFLOWS = 'workflows';

import './styles/ListSidebar.scss';

export type ListSidebarType =
  | 'org-batch'
  | 'group-batch'
  | 'group-profile'
  | 'unclaimed-group-batch'
  | 'unclaimed-group-profile'
  | 'user-profile'
  | 'view-all-announcements'
  | 'workflows';

type WorkflowSidebarData = {
  type: 'workflows';
};

type GroupProfileSidebarData = {
  type: 'group-profile';
  data: {
    group: GroupProfileType;
    entityUuid: string;
    entityType?: string;
  };
};

type UnclaimedGroupProfileSidebarData = {
  type: 'unclaimed-group-profile';
  data: {
    group: PartialUnclaimedGroupProfileType;
    entityUuid: string;
    entityType?: string;
  };
};

type UserProfileSidebarData = {
  type: 'user-profile';
  data: {
    member: UserProfilePartial;
    entityUuid: string;
    entityType?: string;
  };
};

type OrgBatchSidebarData = {
  type: 'org-batch';
  data: OrgBatchProps;
};

type GroupBatchSidebarData = {
  type: 'group-batch';
  data: GroupBatchProps;
};

type UnclaimedGroupBatchSidebarData = {
  type: 'unclaimed-group-batch';
  data: UnclaimedGroupBatchProps;
};

type AnnouncementsSidebarData = {
  type: 'view-all-announcements';
  data: {
    groupUuid: string;
  };
};

export type SidebarData =
  | WorkflowSidebarData
  | GroupProfileSidebarData
  | UnclaimedGroupProfileSidebarData
  | UnclaimedGroupBatchSidebarData
  | UserProfileSidebarData
  | GroupBatchSidebarData
  | OrgBatchSidebarData
  | AnnouncementsSidebarData;

type Props = {
  sidebarData: SidebarData | null;
  reloadQuery: boolean;
  onUserChanged: () => void;
  onClose: () => void;
  onEntityClicked: (sidebarData: SidebarData) => void;
  onBackClicked?: () => void;
  onRemoveGroupFromOrg?: () => void;
  onViewAllAnnouncementsClicked?: () => void;
  openModalRemoveGroupFromOrgEventKey: string;
  isAdmin: boolean;

  userProfileView: React.ComponentType<UserProfileProps>;
  groupProfileView: React.ComponentType<GroupProfileProps>;
};

/* ListSidebar is a drop in component that can be used to display
 * the specific details for any list item. E.g. it is used to
 * display detailed user information when selecting a user row
 * from the People List; it is used to display detailed group
 * information when selecting a group from the Group List.
 * It also handles clicking within the sidebar to load new sidebars.
 * To add a new sidebar type, you need to create a Sidebar data
 * type (like above), provide a handler method that will render
 * the new sidebar, and then add a call to that to the render method.
 */
export default class ListSidebar extends React.Component<Props> {
  handleGroupClicked = (sidebarData: UserProfileSidebarData) => {
    const { entityUuid, entityType } = sidebarData.data;

    return (group: GroupProfileType) => {
      this.props.onEntityClicked({
        type: GROUP_PROFILE,
        data: { group, entityUuid, entityType },
      });
    };
  };

  handleUserClicked = (
    sidebarData: UserProfileSidebarData | GroupProfileSidebarData | UnclaimedGroupProfileSidebarData
  ) => {
    const { entityUuid, entityType } = sidebarData.data;

    return (user: UserProfile, role: OrganizationRole) => {
      this.props.onEntityClicked({
        type: USER_PROFILE,
        data: { member: { ...user, roles: [role] }, entityUuid, entityType },
      });
    };
  };

  handleViewAllAnnouncementsClicked = (groupUuid: string) => {
    const { onViewAllAnnouncementsClicked } = this.props;
    this.props.onEntityClicked({ type: ANNOUNCEMENTS, data: { groupUuid } });

    if (onViewAllAnnouncementsClicked) {
      onViewAllAnnouncementsClicked();
    }
  };

  renderUserProfilePane = (sidebarData: UserProfileSidebarData) => {
    const { data } = sidebarData;
    const {
      userProfileView: UserView,
      onClose,
      onUserChanged,
      reloadQuery,
      onBackClicked,
    } = this.props;

    return (
      <UserView
        data-test={USER_PROFILE}
        member={data.member}
        entityUuid={data.entityUuid}
        entityType={data.entityType}
        onBackClicked={onBackClicked}
        onClose={onClose}
        onFamilyMemberClicked={this.handleUserClicked(sidebarData)}
        onGroupClicked={this.handleGroupClicked(sidebarData)}
        onUserChanged={onUserChanged}
        trackEvent={() => {}}
      />
    );
  };

  renderGroupProfilePane = (sidebarData: GroupProfileSidebarData) => {
    const { data } = sidebarData;
    const {
      groupProfileView: GroupView,
      onClose,
      onUserChanged,
      reloadQuery,
      onEntityClicked,
      onBackClicked,
      onRemoveGroupFromOrg,
      openModalRemoveGroupFromOrgEventKey,
      isAdmin,
    } = this.props;

    return (
      <GroupView
        group={data.group}
        reloadQuery={reloadQuery}
        data-test={GROUP_PROFILE}
        onBackClicked={onBackClicked}
        onClose={onClose}
        onEntityClicked={onEntityClicked}
        onRemoveGroupFromOrg={onRemoveGroupFromOrg}
        onUserChanged={onUserChanged}
        onUserClicked={this.handleUserClicked(sidebarData)}
        onViewAllAnnouncementsClicked={this.handleViewAllAnnouncementsClicked}
        openModalRemoveGroupFromOrgEventKey={openModalRemoveGroupFromOrgEventKey}
        isAdmin={isAdmin}
      />
    );
  };

  renderUnclaimedGroupProfilePane = (sidebarData: UnclaimedGroupProfileSidebarData) => {
    const { data } = sidebarData;
    const { onClose, onUserChanged, reloadQuery, onBackClicked } = this.props;

    return (
      <UnclaimedGroupProfile
        group={data.group}
        reloadQuery={reloadQuery}
        data-test={UNCLAIMED_GROUP_PROFILE}
        onBackClicked={onBackClicked}
        onClose={onClose}
        onUserChanged={onUserChanged}
        onUserClicked={this.handleUserClicked(sidebarData)}
      />
    );
  };

  renderGroupBatchProfilePane = (sidebarData: GroupBatchSidebarData) => {
    return <GroupPeopleListBatchActions data-test={GROUP_BATCH} {...sidebarData.data} />;
  };

  renderUnclaimedGroupBatchProfilePane = (sidebarData: UnclaimedGroupBatchSidebarData) => {
    return (
      <UnclaimedGroupListBatchActions data-test={UNCLAIMED_GROUP_BATCH} {...sidebarData.data} />
    );
  };

  renderOrgBatchProfilePane = (sidebarData: OrgBatchSidebarData) => {
    return <OrgPeopleListBatchActions data-test={ORG_BATCH} {...sidebarData.data} />;
  };

  renderViewAllAnnouncements = (sidebarData: AnnouncementsSidebarData) => {
    const { data } = sidebarData;
    const { onClose, onBackClicked } = this.props;

    return (
      <PeopleListViewAllAnnouncementsDataContainer
        groupUuid={data.groupUuid}
        data-test={ANNOUNCEMENTS}
        onBackClicked={onBackClicked}
        onClose={onClose}
      />
    );
  };

  render() {
    const { sidebarData } = this.props;

    if (!sidebarData) {
      return <CenteredLoadingIndicator size="small" />;
    }

    return (
      <aside className="ListSidebar">
        {sidebarData.type === WORKFLOWS && <span data-test={WORKFLOWS}>Workflows</span>}
        {sidebarData.type === ORG_BATCH && this.renderOrgBatchProfilePane(sidebarData)}
        {sidebarData.type === GROUP_BATCH && this.renderGroupBatchProfilePane(sidebarData)}
        {sidebarData.type === USER_PROFILE && this.renderUserProfilePane(sidebarData)}
        {sidebarData.type === GROUP_PROFILE && this.renderGroupProfilePane(sidebarData)}
        {sidebarData.type === UNCLAIMED_GROUP_PROFILE &&
          this.renderUnclaimedGroupProfilePane(sidebarData)}
        {sidebarData.type === UNCLAIMED_GROUP_BATCH &&
          this.renderUnclaimedGroupBatchProfilePane(sidebarData)}
        {sidebarData.type === ANNOUNCEMENTS && this.renderViewAllAnnouncements(sidebarData)}
      </aside>
    );
  }
}
